import * as Tabs from '@wandb/weave/components/Tabs';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {JSONNaN} from '@wandb/weave/core';
import React, {useEffect, useMemo, useState} from 'react';
import {Redirect, useHistory, useParams} from 'react-router-dom';

import {envIsLocal} from '../../config';
import {LoaderProxy} from '../../containers/AppLoader';
import {useRunDetailsQuery} from '../../generated/graphql';
import NoAccessWrapper from '../../pages/NoAccessPage/NoAccessWrapper';
import RunArtifacts from '../../pages/Run/RunArtifacts';
import RunOverview from '../../pages/Run/RunOverview';
import RunPageHeader from '../../pages/Run/RunPageHeader';
import {useViewer} from '../../state/viewer/hooks';
import {setDocumentDescription, setDocumentTitle} from '../../util/document';
import {AccessOption} from '../../util/permissions';
import * as Run from '../../util/runs';
import * as urls from '../../util/urls';
import AnonymousClaimBanner from '../AnonymousClaimBanner';
import {CodeFiles} from '../CodeFiles';
import {useFetchRunJob} from '../Launch/hooks';
import {JobDrawer} from '../Launch/JobDrawer/JobDrawer';
import NoMatch from '../NoMatch';
import RunFiles from '../RunFiles';
import RunModelInfo from '../RunModelInfo';
import {RunPageLogs} from './RunPageLogs';

type RunParams = {
  entityName: string;
  projectName: string;
  runName: string;
  runTab?: string;
  filePath?: string;
};

type RunTabItem = {
  name: string;
  slug: string;
  render: () => React.ReactNode;
};

export const RunDetails = () => {
  const viewer = useViewer();
  const {entityName, projectName, runName, runTab, filePath} =
    useParams<RunParams>();
  const baseUrl = urls.run({entityName, projectName, name: runName});

  const runDetailsQuery = useRunDetailsQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
  });

  const project = runDetailsQuery.data?.project;
  const run = project?.run;
  const isProjectReadOnly = !viewer || !!project?.readOnly;
  const isPublicOrOpenProject =
    project?.access === AccessOption.Public ||
    project?.access === AccessOption.Open;
  const inputArtifactsCount = run?.inputArtifacts?.totalCount ?? 0;
  const outputArtifactsCount = run?.outputArtifacts?.totalCount ?? 0;
  const hasArtifacts = inputArtifactsCount + outputArtifactsCount > 0;

  // Get the job that created this run (if any)
  const {jobInfo} = useFetchRunJob({
    entityName: project?.entityName ?? '',
    projectName: project?.name ?? '',
    runName: run?.name ?? '',
    skip: run?.inputArtifacts == null || run?.inputArtifacts.totalCount === 0,
  });
  const [jobToRun, setJobToRun] = useState<string | null>(null);
  const cloneRun = (jobId: string) => {
    setJobToRun(jobId);
  };
  const closeDrawer = () => {
    setJobToRun(null);
  };
  const history = useHistory();

  const tabDescription: string = useMemo(() => {
    switch (runTab) {
      case `overview`:
        return `Overview`;
      case `tensorboard`:
        return `Tensorboard`;
      case `model`:
        return `Model`;
      case `models`:
        return `Models`;
      case `logs`:
        return `Logs`;
      case `files`:
        return 'Files';
      case `artifacts`:
        return 'Artifacts';
      case `code`:
        return 'Code';
      default:
        return 'Overview';
    }
  }, [runTab]);

  useEffect(() => {
    if (run == null) {
      return;
    }
    const runDisplayName = run.displayName ?? runName;
    setDocumentTitle(`${runDisplayName} | ${projectName}`);
    setDocumentDescription(
      `${tabDescription} of run ${runDisplayName} in ${projectName}, a machine learning project by ${entityName} using Weights & Biases.`
    );
  }, [tabDescription, run, runName, projectName, entityName]);

  if (runDetailsQuery.loading) {
    return <LoaderProxy name="run-query-loading-loader" />;
  } else if (project && project.entity.claimingEntity != null) {
    // If the run's entity has been claimed, redirect to the claiming entity
    // instead so the original link still works.
    return (
      <Redirect
        to={urls.run({
          entityName: project.entity.claimingEntity.name,
          projectName,
          name: runName,
        })}
      />
    );
  } else if (project == null || run == null) {
    return <NoMatch />;
  }
  const summaryMetrics = Run.parseSummary(run.summaryMetrics, run.name) || {};
  const config = JSONNaN.JSONparseNaN(run.config);
  // 'wandb_version' is an extra thing put in by the CLI that is a misnomer
  // and useless.
  delete config.wandb_version;

  const tabs: RunTabItem[] = [
    {
      name: 'Overview',
      slug: 'overview',
      render: () => (
        <>
          <AnonymousClaimBanner
            entityName={entityName}
            projectName={projectName}
            run={runName}
            createdAt={run.createdAt}
          />
          <RunOverview
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            summaryMetrics={summaryMetrics}
            config={config}
            history={history}
          />
        </>
      ),
    },
    ...(run.servicesAvailable?.tensorboard && !envIsLocal
      ? [
          {
            name: 'Tensorboard',
            slug: 'tensorboard',
            render: () => (
              <iframe
                title="TensorBoard"
                className="tensorboard-iframe"
                src={urls.tensorboardUrl(entityName, projectName, runName)}
              />
            ),
          },
        ]
      : []),
    ...(Object.values(summaryMetrics).find(
      (val: any) => val && (val._type === 'graph' || val._type === 'graph-file')
    )
      ? [
          {
            name: 'Model',
            slug: 'model',
            render: () => (
              <>
                <AnonymousClaimBanner
                  entityName={entityName}
                  projectName={projectName}
                  run={runName}
                  createdAt={run.createdAt}
                />
                <RunModelInfo
                  entityName={entityName}
                  projectName={projectName}
                  runName={runName}
                  sweepName={run.sweep?.name}
                  group={run.group ?? ''}
                  summaryMetrics={summaryMetrics}
                />
              </>
            ),
          },
        ]
      : []),
    {
      name: 'Logs',
      slug: 'logs',
      render: () => (
        <RunPageLogs
          entityName={entityName}
          projectName={projectName}
          runName={runName}
        />
      ),
    },
    {
      name: 'Files',
      slug: 'files',
      render: () => (
        <>
          <AnonymousClaimBanner
            entityName={entityName}
            projectName={projectName}
            run={runName}
            createdAt={run.createdAt}
          />
          <RunFiles
            entityName={entityName}
            projectName={projectName}
            runName={runName}
            path={filePath}
            history={history}
            groupName={run.group ?? ''}
            sweepName={run.sweep?.name}
          />
        </>
      ),
    },
    ...(config._wandb?.value?.code_path || config._wandb?.value?.session_history
      ? [
          {
            name: 'Code',
            slug: 'code',
            render: () => (
              <CodeFiles
                runName={runName}
                entityName={entityName}
                projectName={projectName}
                filePath={filePath ?? ''}
              />
            ),
          },
        ]
      : []),
    ...(hasArtifacts
      ? [
          {
            name: `Artifacts`,
            slug: 'artifacts',
            render: () => (
              <>
                <AnonymousClaimBanner
                  entityName={entityName}
                  projectName={projectName}
                  run={runName}
                  createdAt={run.createdAt}
                />
                <RunArtifacts
                  entityName={entityName}
                  projectName={projectName}
                  runName={runName}
                  groupName={run.group ?? ''}
                  sweepName={run.sweep?.name}
                />
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <Tailwind>
      <div className="run-page px-24 py-16">
        <NoAccessWrapper skipCheck={isPublicOrOpenProject}>
          <div className="mb-12 font-semibold">
            <RunPageHeader
              readOnly={isProjectReadOnly}
              entityName={project.entityName}
              projectName={project.name}
              runName={run.name}
              jobId={jobInfo?.id}
              cloneRun={cloneRun}
            />
          </div>
          <JobDrawer
            open={jobToRun != null}
            closeDrawer={closeDrawer}
            entityName={project.entityName}
            projectName={project.name}
            defaultJobId={jobToRun ?? ''}
            defaultJobVersion={jobInfo?.versionStr ?? ''}
            defaultRunToClone={run.name}
          />

          <Tabs.Root value={runTab ?? 'overview'}>
            <Tabs.List>
              {tabs.map(tab => (
                <Tabs.LinkedTrigger
                  key={tab.slug}
                  value={tab.slug}
                  to={`${baseUrl}/${tab.slug}`}>
                  {tab.name}
                </Tabs.LinkedTrigger>
              ))}
            </Tabs.List>
            <div className="py-12">
              {tabs.map(tab => (
                <Tabs.Content
                  className="dark:text-moon-200"
                  key={tab.slug}
                  value={tab.slug}>
                  {tab.render()}
                </Tabs.Content>
              ))}
            </div>
          </Tabs.Root>
        </NoAccessWrapper>
      </div>
    </Tailwind>
  );
};
